import React, {createContext, FC, useContext, useMemo} from 'react';
import type {Request} from 'express';

const LegacyRootContext = createContext({
  serverRequest: undefined as Request | undefined,
});

export const useLegacyServerRequest = () => {
  return useContext(LegacyRootContext).serverRequest;
};

interface LegacyRootProviderProps {
  /**
   * Server Request should only be provided on the server, never on the client.
   * It is used to generate the initial state for components that needs it.
   */
  serverRequest?: Request;
}

export const LegacyRootProvider: FC<
  React.PropsWithChildren<LegacyRootProviderProps>
> = ({children, serverRequest}) => {
  if (process.browser && serverRequest) {
    throw new Error(
      'LegacyRootProvider received a `serverRequest` property on the client. This property should only be used on the server render.',
    );
  }

  if (!process.browser && !serverRequest) {
    throw new Error(
      'LegacyRootProvider requires a `serverRequest` property on the server, but none was provided.',
    );
  }

  const value = useMemo(() => {
    return {serverRequest};
  }, [serverRequest]);

  return (
    <LegacyRootContext.Provider value={value}>
      {children}
    </LegacyRootContext.Provider>
  );
};
